import React, { useEffect, useState } from "react";

import { Link, useParams } from "react-router-dom";

import { collection, getDocs, limit, query, where } from "firebase/firestore";

import {
  FaArrowLeft,
  FaCalendarAlt,
  FaUser,
  FaTag,
  FaBlog,
} from "react-icons/fa";

import { db } from "../../firebase/firebase";

import { formatBlogDate } from "../../data/blogs";

import "./BlogDetails.css";

const BlogDetails = () => {
  const { slug } = useParams();

  const [blog, setBlog] = useState(null);

  const [loading, setLoading] = useState(true);

  const [notFound, setNotFound] = useState(false);

  // ============================================================
  // LOAD BLOG
  // ============================================================

  useEffect(() => {
    const loadBlog = async () => {
      try {
        setLoading(true);

        setNotFound(false);

        if (!slug) {
          setNotFound(true);

          return;
        }

        const decodedSlug = decodeURIComponent(slug);

        const blogsRef = collection(db, "blogs");

        const blogQuery = query(
          blogsRef,

          where("slug", "==", decodedSlug),

          where("published", "==", true),

          limit(1),
        );

        const snapshot = await getDocs(blogQuery);

        if (snapshot.empty) {
          setNotFound(true);

          setBlog(null);

          return;
        }

        const blogDocument = snapshot.docs[0];

        setBlog({
          id: blogDocument.id,

          ...blogDocument.data(),
        });
      } catch (err) {
        console.error("Error loading blog:", err);

        setNotFound(true);

        setBlog(null);
      } finally {
        setLoading(false);
      }
    };

    loadBlog();
  }, [slug]);

  // ============================================================
  // LOADING
  // ============================================================

  if (loading) {
    return (
      <div className="public-blog-loading">
        <div className="public-blog-spinner"></div>

        <p>Loading article...</p>
      </div>
    );
  }

  // ============================================================
  // NOT FOUND
  // ============================================================

  if (notFound || !blog) {
    return (
      <div className="blog-not-found">
        <div className="blog-not-found-icon">
          <FaBlog />
        </div>

        <h1>Blog Not Found</h1>

        <p>
          The blog you're looking for doesn't exist or has not been published
          yet.
        </p>

        <Link to="/blogs" className="back-to-blogs-btn">
          <FaArrowLeft />
          Back to Blogs
        </Link>
      </div>
    );
  }

  // ============================================================
  // ARTICLE CONTENT
  // ============================================================

  const paragraphs = (blog.content || "")
    .split(/\n+/)
    .filter((paragraph) => paragraph.trim());

  // ============================================================
  // PAGE
  // ============================================================

  return (
    <main className="blog-details-page">
      {/* ======================================================
          HERO
      ====================================================== */}

      <section className="blog-details-hero">
        {blog.image ? (
          <img
            src={blog.image}
            alt={blog.title || "Blog"}
            className="blog-details-hero-image"
          />
        ) : (
          <div className="blog-details-no-image">
            <FaBlog />
          </div>
        )}

        <div className="blog-details-hero-overlay"></div>

        <div className="blog-details-hero-content">
          <span className="blog-details-category">
            <FaTag />

            {blog.category || "Sports"}
          </span>

          <h1>{blog.title || "Untitled Blog"}</h1>

          <div className="blog-details-meta">
            <span>
              <FaUser />

              {blog.author || "Quality Sports Surface"}
            </span>

            <span>
              <FaCalendarAlt />

              {formatBlogDate(blog.date || blog.publishedAt || blog.createdAt)}
            </span>
          </div>
        </div>
      </section>

      {/* ======================================================
          CONTENT
      ====================================================== */}

      <section className="blog-details-container">
        <div className="blog-details-main">
          {/* BACK */}

          <Link to="/blogs" className="blog-back-link">
            <FaArrowLeft />
            Back to Blogs
          </Link>

          {/* EXCERPT */}

          {blog.excerpt && (
            <div className="blog-details-excerpt">{blog.excerpt}</div>
          )}

          {/* ARTICLE */}

          <article className="blog-article">
            {paragraphs.length > 0 ? (
              paragraphs.map((paragraph, index) => (
                <p key={index}>{paragraph}</p>
              ))
            ) : (
              <p>No content available for this article.</p>
            )}
          </article>

          {/* TAGS */}

          {Array.isArray(blog.tags) && blog.tags.length > 0 && (
            <div className="blog-details-tags">
              <strong>Tags:</strong>

              <div>
                {blog.tags.map((tag, index) => (
                  <span key={index}>{tag}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ======================================================
          CTA
      ====================================================== */}

      <section className="blog-details-cta">
        <div className="blog-details-cta-inner">
          <div>
            <h2>Need a Sports Surface for Your Project?</h2>

            <p>
              Talk to Quality Sports Surface about your next sports
              infrastructure project.
            </p>
          </div>

          <Link to="/contact" className="blog-contact-btn">
            Get In Touch
          </Link>
        </div>
      </section>
    </main>
  );
};

export default BlogDetails;
